import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function LongTermPage() {
  const benefits = [
    {
      icon: 'ri-home-4-line',
      title: 'Quality Homes',
      description: 'Well-maintained properties in desirable locations, professionally managed to the highest standards.'
    },
    {
      icon: 'ri-file-text-line',
      title: 'Transparent Leases',
      description: 'Clear, straightforward lease agreements with no hidden fees or surprises. Everything is documented and explained.'
    },
    {
      icon: 'ri-tools-line',
      title: 'Reliable Maintenance',
      description: 'Prompt response to maintenance requests with professional repairs and regular property inspections.'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Dedicated Support',
      description: 'Responsive property management team available to address concerns and ensure your comfort.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Safe & Secure',
      description: 'Properties equipped with modern security features and maintained to ensure tenant safety and peace of mind.'
    },
    {
      icon: 'ri-price-tag-3-line',
      title: 'Fair Pricing',
      description: 'Competitive rental rates with transparent pricing and flexible lease terms to meet your needs.'
    }
  ];

  const features = [
    'Professional Property Management',
    'Online Rent Payment Portal',
    'Quick Maintenance Response',
    'Regular Property Inspections',
    'Clear Communication Channels',
    'Flexible Lease Options',
    'Pet-Friendly Properties Available',
    'Move-In Ready Homes',
    'Utilities Guidance & Support',
    'Neighborhood Information',
    'Lease Renewal Options',
    'Respectful Tenant Community'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section 
        className="relative bg-cover bg-center h-96 flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/6433602f99605355bae50c68682c00b5.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Long-Term Rentals</h1>
          <p className="text-2xl text-gray-100 max-w-3xl">
            Your future home, professionally managed with care
          </p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-teal-50 to-blue-50 border-l-4 border-teal-600 rounded-lg p-8 mb-16 shadow-lg">
            <div className="flex items-start space-x-4">
              <i className="ri-information-line text-3xl text-teal-600 flex-shrink-0"></i>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Coming Soon: Long-Term Residential Solutions</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  While we currently specialize in short-term rentals, we're actively building our long-term residential rental program. Our expansion into long-term housing will bring the same professional management, quality standards, and tenant care that define our short-term operations.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Interested in being notified when long-term rentals become available? Contact us to join our waiting list and be among the first to access our professionally managed long-term properties.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What to Expect from ARM Long-Term Rentals</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              When our long-term rental program launches, tenants will enjoy the same exceptional service and quality that defines our short-term operations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-blue-600 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src="https://readdy.ai/api/search-image?query=Happy%20diverse%20family%20moving%20into%20new%20apartment%2C%20smiling%20couple%20with%20keys%20in%20modern%20living%20room%20with%20colorful%20decor%2C%20bright%20and%20welcoming%20space%2C%20professional%20property%20management%2C%20comfortable%20home%20environment%2C%20natural%20lighting%2C%20warm%20atmosphere%2C%20successful%20tenant%20experience%2C%20vibrant%20inviting%20colors&width=800&height=600&seq=arm-tenant-v2&orientation=landscape"
                alt="Future Tenants"
                className="rounded-xl shadow-2xl object-cover w-full h-96"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">The ARM Tenant Experience</h2>
              <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                <p>
                  Our long-term rental program will provide tenants with professionally managed homes that prioritize comfort, safety, and quality of life.
                </p>
                <p>
                  From the initial application process through lease signing and throughout your tenancy, you'll experience the same attention to detail and responsive service that makes ARM a trusted name in residential management.
                </p>
                <p>
                  We believe in building lasting relationships with our tenants, maintaining open communication, and ensuring every property meets our high standards for safety, cleanliness, and livability.
                </p>
                <p className="font-semibold bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">
                  Join our waiting list today to be notified when long-term rentals become available in your area.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Future Long-Term Rental Features</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              When we launch our long-term program, tenants will enjoy these benefits and services
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center space-x-3 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-4 hover:shadow-md transition-all duration-300">
                <i className="ri-checkbox-circle-fill text-2xl text-teal-600"></i>
                <span className="text-gray-900 font-medium">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-12">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Commitment to Future Tenants</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                As we expand into long-term rentals, we're committed to providing the same exceptional service that defines our short-term operations
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-shield-star-line text-4xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Quality Assurance</h3>
                <p className="text-gray-600">Every property maintained to our strict standards with regular inspections and prompt maintenance.</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-user-heart-line text-4xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Tenant-Focused</h3>
                <p className="text-gray-600">Your comfort and satisfaction are our priorities, with responsive support and clear communication.</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-file-shield-line text-4xl text-purple-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Professional Standards</h3>
                <p className="text-gray-600">Licensed, compliant, and transparent operations you can trust for your long-term housing needs.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-br from-teal-600 via-blue-600 to-purple-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Interested in Long-Term Rentals?</h2>
          <p className="text-xl text-teal-50 mb-10">
            Join our waiting list to be notified when our long-term rental program launches. Be among the first to access professionally managed, quality homes for your long-term housing needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="mailto:Staying.arm@gmail.com?subject=Long-Term Rental Waiting List"
              className="px-8 py-4 bg-white text-teal-700 font-semibold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Join Waiting List
            </a>
            <Link 
              to="/short-term"
              className="px-8 py-4 bg-purple-800 text-white font-semibold rounded-lg hover:bg-purple-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              View Short-Term Rentals
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
